import { mapLineChartData } from '../../../components/Charts/LineChart/mapLineChartData';
import { LineChartData, TQuoter } from '../../../types';

export interface DatedValue {
  date: Date;
  value: number;
}

// Buckets dated values onto a quarter axis and returns the running total per quarter, which
// is what every "over time" chart on the portfolio page draws.
//
// Values dated before the axis start fold into its first quarter, so a capped axis still
// reports a true cumulative total. Values in the current partial quarter fold into the
// trailing as-of point (see QuartersBuilder.asOfQuarter); values after it are dropped.
export function cumulativeQuarterSeries(
  rows: DatedValue[],
  axisQuarters: TQuoter[],
  seriesName: string
): LineChartData[] {
  const oldest = axisQuarters.at(-1);
  const axisStart = oldest?.start.getTime() ?? -Infinity;
  const firstLabel = oldest?.label ?? '';
  const newest = axisQuarters[0];

  // Seeded oldest-first, so the running total below needs no sort — `axisQuarters` is
  // newest-first and Map keeps insertion order, as does mapLineChartData's grouping.
  const buckets = new Map<string, number>();
  for (let i = axisQuarters.length - 1; i >= 0; i -= 1) {
    buckets.set(axisQuarters[i].label, 0);
  }

  rows.forEach(({ date, value }) => {
    const quarterLabel =
      date.getTime() < axisStart
        ? firstLabel
        : `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
    // A quarter missing from the axis is the current partial one: it lands on the as-of point.
    const label =
      !buckets.has(quarterLabel) && newest && date.getTime() <= newest.end.getTime()
        ? newest.label
        : quarterLabel;
    const bucket = buckets.get(label);

    if (bucket !== undefined) {
      buckets.set(label, bucket + value);
    }
  });

  let runningTotal = 0;

  return mapLineChartData(
    [...buckets].map(([period, value]) => {
      runningTotal += value;

      return { period, [seriesName]: runningTotal };
    })
  );
}
